import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
// import { colorMode_Store } from "./store/actions/mainAction";
import { color } from "@mui/system";

// color design tokens export
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0",
          200: "#c2c2c2",
          300: "#a3a3a3",
          400: "#858585",
          500: "#666666",
          600: "#525252",
          700: "#3d3d3d",
          800: "#292929",
          900: "#141414",
        },
        primary: {
          100: "#ffffff",
          200: "#cbd5e1",
          300: "#9ca3af",
          400: "#0f1220",
          500: "#8b5cf6",
          600: "#080b16",
          700: "#050711",
          800: "#03040a",
          900: "#000000",
        },
        primaryAlpa: {
          500: "#141b2d73",
        },
        greenAccent: {
          100: "#ecfeff",
          200: "#cffafe",
          300: "#67e8f9",
          400: "#22d3ee",
          500: "#06b6d4",
          600: "#0891b2",
          700: "#0e7490",
          800: "#155e75",
          900: "#164e63",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#dbeafe",
          200: "#bfdbfe",
          300: "#93c5fd",
          400: "#60a5fa",
          500: "#3b82f6",
          600: "#2563eb",
          700: "#1d4ed8",
          800: "#1e40af",
          900: "#1e3a8a",
        },
      }
    : {
        grey: {
          100: "#141414",
          200: "#292929",
          300: "#3d3d3d",
          400: "#525252",
          500: "#666666",
          600: "#858585",
          700: "#a3a3a3",
          800: "#c2c2c2",
          900: "#e0e0e0",
        },
        primary: {
          100: "#0c1220",
          200: "#1d4fbb",
          300: "#2f6fed",
          400: "#f6f8fc",
          500: "#3b82f6",
          600: "#60a5fa",
          700: "#8492a6",
          800: "#c4ccda",
          900: "#ffffff",
        },
        primaryAlpa: {
          500: "#141b2d73",
        },
        greenAccent: {
          100: "#164e63",
          200: "#155e75",
          300: "#0e7490",
          400: "#0891b2",
          500: "#06b6d4",
          600: "#22d3ee",
          700: "#67e8f9",
          800: "#cffafe",
          900: "#ecfeff",
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },
        blueAccent: {
          100: "#1e3a8a",
          200: "#1e40af",
          300: "#1d4ed8",
          400: "#2563eb",
          500: "#3b82f6",
          600: "#60a5fa",
          700: "#93c5fd",
          800: "#bfdbfe",
          900: "#dbeafe",
        },
      }),
});

// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            // palette values for dark mode
            primary: {
              main: colors.primary[500],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              // default: colors.primary[500],
              default: '#050711',
              alpha: colors.primaryAlpa[500],
              paper: '#0f1220',
            },
            border: {
              default: '#22d3ee'
            }
          }
        : {
            // palette values for light mode
            primary: {
              main: colors.primary[100],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: "#f6f8fc",
              alpha: "#ffffffbf",
              paper: '#ffffff',
            },
            border: {
              default: '#2f6fed'
            }
        }),
    },
    typography: {
      fontFamily: ["Poppins", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Poppins", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Poppins", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Poppins", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Poppins", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Poppins", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Poppins", "sans-serif"].join(","),
        fontSize: 14,
      },
    },
    components: (mode === 'dark')? {
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .Mui-focused fieldset':{
              borderColor:"#fff !important"},
            '& .MuiInputLabel-outlined':{
              color:"#fff !important"
            }
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            '& .Mui-focused fieldset':{
              borderColor:"#fff !important"},
            '& .Mui-focused':{
              color:"#fff !important"
            }
          },
        },
      },
      MuiButton:{
        styleOverrides: {
          root: {
            color: '#fff'
          }
        }
      }
    }:{
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .Mui-focused fieldset':{
              borderColor:"#6F7E8C !important"},
            '& .MuiInputLabel-outlined':{
              color:"#6F7E8C !important"
            }
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            '& .Mui-focused fieldset':{
              borderColor:"#6F7E8C !important"},
            '& .Mui-focused':{
              color:"#6F7E8C !important"
            }
          },
        },
      },
      // MuiButton:{
      //   styleOverrides: {
      //     root: {
      //       color: '#141b2d'
      //     }
      //   }
      // }
    }
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = () => {
//   const colorModeName = useSelector(store => store.colorModeName);
  const modeStr = localStorage.getItem('colorMode');
  const [mode, setMode] = useState(modeStr? modeStr : 'dark');
  const dispatch = useDispatch();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>{
        //console.log('toggleColorMode');
        setMode((prev) => (prev === "light" ? "dark" : "light"))
        // dispatch(colorMode_Store(mode === "light" ? "dark" : "light"));
        localStorage.setItem('colorMode', mode === "light" ? "dark" : "light");
      },
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};
